/**
 * Command palette (⌘K).
 *
 * The primary navigation tool for an experienced operator who already knows where
 * they are going. Lists only destinations the user's permissions allow, and
 * searches inspections by id, unit, project or customer.
 */

import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ArrowRight, ClipboardList, CornerDownLeft } from 'lucide-react'
import * as Dialog from '@radix-ui/react-dialog'
import { cn } from '@/lib/cn'
import { useTenant } from '@/app/providers/TenantProvider'
import { visibleNavigation } from '@/app/config/navigation'
import { inspectionsApi } from '@/services/api/inspections'
import { useDebounce } from '@/hooks/useDebounce'
import { PERMISSIONS } from '@/lib/permissions'
import { StatusBadge } from '@/components/data-display/StatusBadge'

interface Entry {
  id: string
  label: string
  hint?: string
  to: string
  icon?: React.ReactNode
}

export function CommandPalette({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const navigate = useNavigate()
  const { permissions } = useTenant()
  const [query, setQuery] = useState('')
  const [active, setActive] = useState(0)
  const debounced = useDebounce(query, 250)

  useEffect(() => {
    if (!open) {
      setQuery('')
      setActive(0)
    }
  }, [open])

  const pages = useMemo<Entry[]>(
    () =>
      visibleNavigation(permissions).flatMap((group) =>
        group.items.map((item) => ({
          id: item.to,
          label: item.label,
          hint: group.label ?? undefined,
          to: item.to,
          icon: <item.icon className="size-4" aria-hidden />,
        })),
      ),
    [permissions],
  )

  const canSearchInspections =
    permissions.includes(PERMISSIONS.INSPECTION_VIEW) && debounced.trim().length >= 2

  const { data: inspections } = useQuery({
    queryKey: ['palette', 'inspections', debounced],
    queryFn: ({ signal }) => inspectionsApi.list({ q: debounced, limit: 5 }, signal),
    enabled: open && canSearchInspections,
    staleTime: 30_000,
  })

  const filteredPages = useMemo(() => {
    const term = debounced.trim().toLowerCase()
    if (!term) return pages
    return pages.filter((page) => page.label.toLowerCase().includes(term))
  }, [pages, debounced])

  const inspectionEntries = useMemo<Entry[]>(
    () =>
      (inspections?.data ?? []).map((inspection) => ({
        id: inspection.id,
        label: `${inspection.unit.number ?? inspection.unit.label} — ${inspection.unit.project_name}`,
        hint: inspection.inspector.name,
        to: `/inspections/${inspection.id}`,
        icon: <ClipboardList className="size-4" aria-hidden />,
      })),
    [inspections],
  )

  const all = [...filteredPages, ...inspectionEntries]

  useEffect(() => setActive(0), [debounced, all.length])

  function go(entry: Entry | undefined) {
    if (!entry) return
    onOpenChange(false)
    navigate(entry.to)
  }

  function onKeyDown(event: React.KeyboardEvent) {
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setActive((index) => Math.min(index + 1, all.length - 1))
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      setActive((index) => Math.max(index - 1, 0))
    } else if (event.key === 'Enter') {
      event.preventDefault()
      go(all[active])
    }
  }

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-fg/30 backdrop-blur-[1px]" />
        <Dialog.Content
          className="fixed left-1/2 top-[15vh] z-50 w-[calc(100vw-2rem)] max-w-xl -translate-x-1/2 overflow-hidden rounded-xl border border-border bg-surface shadow-lg"
          onKeyDown={onKeyDown}
        >
          <Dialog.Title className="sr-only">Search and navigate</Dialog.Title>
          <Dialog.Description className="sr-only">
            Type to filter pages, or search inspections by id, unit, project or customer.
          </Dialog.Description>

          <input
            autoFocus
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Jump to a page, or search inspections…"
            aria-label="Search"
            className="w-full border-b border-border bg-transparent px-4 py-3 text-sm outline-none placeholder:text-fg-disabled"
          />

          <div role="listbox" aria-label="Results" className="scrollbar-thin max-h-80 overflow-y-auto p-1.5">
            {all.length === 0 ? (
              <p className="px-3 py-6 text-center text-sm text-fg-muted">
                {debounced ? 'Nothing matched.' : 'Start typing to search.'}
              </p>
            ) : (
              all.map((entry, index) => {
                const isInspection = inspectionEntries.includes(entry)
                const inspection = isInspection
                  ? inspections?.data.find((item) => item.id === entry.id)
                  : undefined
                return (
                  <button
                    key={`${entry.id}-${index}`}
                    type="button"
                    role="option"
                    aria-selected={index === active}
                    onMouseEnter={() => setActive(index)}
                    onClick={() => go(entry)}
                    className={cn(
                      'flex w-full items-center gap-3 rounded-md px-3 py-2 text-left text-sm',
                      index === active ? 'bg-surface-sunken text-fg' : 'text-fg-muted',
                    )}
                  >
                    <span className="text-fg-subtle">{entry.icon}</span>
                    <span className="min-w-0 flex-1 truncate">{entry.label}</span>
                    {inspection ? <StatusBadge status={inspection.status} size="sm" withTooltip={false} /> : null}
                    {entry.hint && !inspection ? (
                      <span className="shrink-0 text-2xs text-fg-subtle">{entry.hint}</span>
                    ) : null}
                    {index === active ? (
                      <CornerDownLeft className="size-3 shrink-0 text-fg-subtle" aria-hidden />
                    ) : (
                      <ArrowRight className="size-3 shrink-0 opacity-0" aria-hidden />
                    )}
                  </button>
                )
              })
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
